import { type FC, useCallback } from "react";
import styled from "styled-components";

export interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (range: { startDate: string; endDate: string }) => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DateLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--vmx-gray-700);
  font-family: var(--vmx-font);
`;

const DateInput = styled.input`
  padding: 10px 14px;
  border: 1px solid var(--vmx-gray-300);
  border-radius: var(--vmx-radius);
  font-family: var(--vmx-font);
  font-size: 0.875rem;
  color: var(--vmx-gray-800);
  background: var(--vmx-white);
  transition: all var(--vmx-transition);
  outline: none;

  &:focus {
    border-color: var(--vmx-accent);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.12);
  }
`;

export const DateRangePicker: FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const maxDate = today();

  const handleStartChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newStart = e.target.value;
      if (newStart > endDate) {
        onChange({ startDate: endDate, endDate: newStart });
      } else {
        onChange({ startDate: newStart, endDate });
      }
    },
    [endDate, onChange]
  );

  const handleEndChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEnd = e.target.value;
      if (newEnd < startDate) {
        onChange({ startDate: newEnd, endDate: startDate });
      } else {
        onChange({ startDate, endDate: newEnd });
      }
    },
    [startDate, onChange]
  );

  return (
    <Wrapper>
      <DateLabel htmlFor="vmx-date-start">From</DateLabel>
      <DateInput
        id="vmx-date-start"
        type="date"
        value={startDate}
        max={maxDate}
        onChange={handleStartChange}
      />
      <DateLabel htmlFor="vmx-date-end">To</DateLabel>
      <DateInput
        id="vmx-date-end"
        type="date"
        value={endDate}
        max={maxDate}
        onChange={handleEndChange}
      />
    </Wrapper>
  );
};
